// src/pages/TaskList.jsx

import { colors as themeColors } from '../utils/theme';
import { useState, useEffect, useRef ,useMemo  } from 'react';
import axios from 'axios';
import { VscNewFile } from "react-icons/vsc";
import { Box, Grid, Paper, Typography, IconButton, Snackbar, Alert, Tooltip, Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Card, CardContent, Checkbox, InputAdornment, TextField, Divider, LinearProgress, } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddTaskIcon from '@mui/icons-material/AddTask';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NoDataComponent from '../components/NoDataComponent';
import CustomNoDataOverlay from '../components/CustomNoDataOverlay';
import CenterLoader from '../components/CenterLoader';
import { useTheme, useMediaQuery } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { colors, gradients } from '../utils/theme';


export default function TaskList({ connectionString }) {
    const navigate = useNavigate();

    const API = connectionString;
    const path = `${API}/api/tasks`;
    const { user, selectedCompany } = useAuth();
    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const role = user?.UserCategory;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));


    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [selectionModel, setSelectionModel] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [pendingDeleteIds, setPendingDeleteIds] = useState([])
    const [deleting, setDeleting] = useState(false);
    const selectedIdsRef = useRef([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [mobileSearchQuery, setMobileSearchQuery] = useState('');
    const [selectAll, setSelectAll] = useState(false);

    console.log("selectionModel", selectionModel)
    const [filters] = useState({
        status: '',
        assignedTo: '',
        createdBy: '',
    });

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel]);

    const filteredMobileRows = useMemo(() => {
    if (!mobileSearchQuery.trim()) return rows;

    const q = mobileSearchQuery.toLowerCase();

    return rows.filter((row) =>
        row.TaskTitle?.toLowerCase().includes(q) || 
        //row.Description?.toLowerCase().includes(q) ||
        // row.AssignedToName?.toLowerCase().includes(q) ||
        row.Status?.toLowerCase().includes(q) ||
        row.Priority?.toLowerCase().includes(q)
    );
}, [rows, mobileSearchQuery]);


    const toggleSelectAll = () => {
        if (selectAll) {
           setSelectionModel([]);
            selectedIdsRef.current = [];
            setSelectAll(false);
        } else {
            const allIds = rows.map(row => row.TaskId);
            setSelectionModel(allIds);
            selectedIdsRef.current = allIds;
            setSelectAll(true);
        }
    };


    const fetchData = async () => {
        setLoading(true);
        try {
            const offset = paginationModel.page * paginationModel.pageSize;

            const res = await axios.get(path, { params: { ...filters, offset, limit: paginationModel.pageSize, userId, companyId, role, }, headers: { 'Cache-Control': 'no-store', }, });

            const countRes = await axios.get(`${API}/api/tasks/count`, { params: { companyId, userId, role }, });

            setRows(res.data.records || []);
            setTotalCount(countRes.data.count || 0);
        } catch (err) {
            console.error('Error fetching tasks:', err);
            setSnackbar({ open: true, message: 'Failed to fetch tasks.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    console.log("selectedIdsRef", selectedIdsRef)

    const handleDelete = async () => {

        if (selectedIdsRef.current.length === 0 || deleting) return;

        setDeleting(true);
        try {
            const res = await axios.post(`${path}/delete`, { userId, companyId, taskIds: selectedIdsRef.current, });

            if (res.status === 200) {
                setSnackbar({ open: true, message: 'Deleted Successfully.', severity: 'success' });
                fetchData();
                setSelectionModel([]);
                selectedIdsRef.current = [];
            } else {
                setSnackbar({ open: true, message: res.data?.message || 'Delete failed.', severity: 'error', });
            }
        } catch (err) {
            console.error('Delete error:', err);
            setSnackbar({ open: true, message: 'Delete failed due to an unexpected error.', severity: 'error', });
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
            setPendingDeleteIds([]);
        }
    };

    const columns = [
        { field: 'TaskTitle', headerName: 'Task', flex: 1.5,
            renderCell: (params) => {
                const row = params.row;
                const createdDate = row.CreatedAt ? new Date(row.CreatedAt).toLocaleString('en-GB') : '-';

                return (
                    <Box sx={{ cursor: 'pointer', whiteSpace: 'normal', wordBreak: 'break-word', my: 1 }} onClick={() => navigate('/main/TaskMaster', { state: { mode: 'edit', taskData: row }, }) } >
                        <Typography variant="subtitle2" sx={{ cursor: 'pointer', color: colors.primary.main, textDecoration: 'underline', fontSize: '0.85rem' }} >
                            {row.TaskTitle || '-'}
                        </Typography>
                        {row.Description && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', }} >
                                {row.Description}
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            Created: {createdDate}
                        </Typography>
                    </Box>
                );
            },
        },
        { field: 'AssignedToName', headerName: 'Assigned To', flex: 0.7,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" fontWeight={600}>
                        {params.value || '—'}
                    </Typography>
                    {params.row.AssignedByName && (
                        <Typography variant="caption" color="text.secondary">
                            By: {params.row.AssignedByName}
                        </Typography>
                    )}
                </Box>
            ),
        },
        { field: 'DueDateTime', headerName: 'Due Date & Time', filterable: false, flex: 0.9,
            renderCell: (params) => {
                const due = params.value ? new Date(params.value).toLocaleString('en-GB') : '—';
                return (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {due}
                    </Typography>
                );
            },
        },
        { field: 'Priority', headerName: 'Priority', flex: 0.4,
            renderCell: (params) => {
                const priority = (params.value || '').toLowerCase();
                let color = '#43a047'; // low
                if (priority === 'high') color = '#e53935';
                else if (priority === 'mid' || priority === 'medium') color = '#fbc02d';
                return (
                    <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
                        {params.value || '—'}
                    </Typography>
                );
            },
        },
        { field: 'Status', headerName: 'Status', flex: 0.5,
            renderCell: (params) => {
                const status = (params.value || '').toLowerCase();
                let chipStyles = {};
                if (status === 'completed') chipStyles = { bgcolor: 'success.main', color: themeColors.neutral.surface };
                else if (status === 'cancelled') chipStyles = { bgcolor: themeColors.error.dark, color: themeColors.neutral.surface };
                else chipStyles = { bgcolor: '#FF4C00', color: themeColors.neutral.surface };
                return <Chip label={params.value || '—'} size="small" sx={{ fontWeight: 600, ...chipStyles }} />;
            },
        },
        {
            field: 'actions',
            headerName: '',
            align: 'right',
            headerAlign: 'right',
            flex: 0.3,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const isSelected = selectionModel.includes(params.row.TaskId);
                return (
                    <Tooltip title="Delete">
                        <span>
                            <IconButton color="error" onClick={(e) => {
                                e.stopPropagation();

                                if (!selectedIdsRef.current.length) return;

                                setConfirmOpen(true);   // 🔥 open dialog
                            }}
                                disabled={!isSelected}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        },
    ];

    // const renderMobileCards = () => (
    //     <Stack spacing={1.5} sx={{ p: 1 }}>
    //         {rows.map((row) => {
    //             const isSelected = selectionModel.includes(row.TaskId);

    //             return (
    //                 <Paper
    //                     key={row.TaskId}
    //                     sx={{
    //                         p: 1.5,
    //                         borderRadius: 2,
    //                         border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
    //                         bgcolor: isSelected ? '#f0f7ff' : '#fff',
    //                     }}
    //                     onClick={() => {
    //                         // toggle selection
    //                         setSelectionModel((prev) => {
    //                             const updated = prev.includes(row.TaskId)
    //                                 ? prev.filter(id => id !== row.TaskId)
    //                                 : [...prev, row.TaskId];

    //                             selectedIdsRef.current = updated;
    //                             return updated;
    //                         });
    //                     }}
    //                 >
    //                     <Stack spacing={0.5}>
    //                         <Typography variant="subtitle2" fontWeight={600}>
    //                             {row.TaskTitle || '—'}
    //                         </Typography>

    //                         {row.Description && (
    //                             <Typography variant="body2" color="text.secondary">
    //                                 {row.Description}
    //                             </Typography>
    //                         )}

    //                         <Typography variant="caption" color="text.secondary">
    //                             Due: {row.DueDateTime
    //                                 ? new Date(row.DueDateTime).toLocaleString('en-GB')
    //                                 : '—'}
    //                         </Typography>

    //                         <Stack direction="row" justifyContent="space-between" alignItems="center">
    //                             <Chip
    //                                 size="small"
    //                                 label={row.Status || '—'}
    //                                 sx={{ fontWeight: 600 }}
    //                             />

    //                             <IconButton
    //                                 color="error"
    //                                 disabled={!isSelected}
    //                                 onClick={(e) => {
    //                                     e.stopPropagation();
    //                                     if (!selectedIdsRef.current.length) return;
    //                                     setConfirmOpen(true);
    //                                 }}
    //                             >
    //                                 <DeleteIcon fontSize="small" />
    //                             </IconButton>
    //                         </Stack>
    //                     </Stack>
    //                 </Paper>
    //             );
    //         })}
    //     </Stack>
    // );

    const renderMobileCards = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (filteredMobileRows.length === 0) {
    return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
            <CustomNoDataOverlay />
        </Box>
    );
}

        if (rows.length === 0) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CustomNoDataOverlay />
                    <Typography sx={{ mt: 1, fontSize: '0.85rem', color: 'text.secondary' }}>
                        Page: {paginationModel.page + 1} | Total: {totalCount}
                    </Typography>
                </Box>
            );
        }



        return (
            <>
                {filteredMobileRows.map((row) => {
                    const isSelected = selectionModel.includes(row.TaskId);
                    let color = '#43a047'; // low
                    if (row.Priority === 'high') color = '#e53935';
                    else if (row.Priority === 'mid' || row.Priority === 'medium') color = '#fbc02d';

                    let chipStyles = {}
                    if (row.Status === 'completed') chipStyles = { bgcolor: 'success.main', color: themeColors.neutral.surface };
                    else if (row.Status === 'cancelled') chipStyles = { bgcolor: themeColors.error.dark, color: themeColors.neutral.surface };
                    else chipStyles = { bgcolor: '#FF4C00', color: themeColors.neutral.surface };

                    return (
                        <Card
                            key={row.TaskId}
                            sx={{
                                mb: 1.5,
                                boxShadow: 3,
                                borderRadius: 2,
                                overflow: 'hidden',
                                transition: '0.2s',
                                '&:hover': { boxShadow: 5 },
                                borderLeft: '4px solid',
                                borderLeftColor: 'primary.main',
                                cursor: 'pointer'
                            }}
                            
                        >
                            <CardContent sx={{ py: 1.8, px: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2
                                    }}
                                >
                                    {/* LEFT SIDE */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1 }}>
                                        { role !== 'User' && <Checkbox
                                            size="small"
                                            checked={isSelected}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() =>
                                                setSelectionModel((prev) => {
                                                    const updated = isSelected
                                                        ? prev.filter((id) => id !== row.TaskId)
                                                        : [...prev, row.TaskId]
                                                    selectedIdsRef.current = updated; // 🔥 freeze selection
                                                    return updated;
                                                })
                                            }
                                            sx={{ mt: 0.4 }}
                                        />}

                                        <Box>
                                            {/* Task Title */}
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={600}
                                                sx={{ cursor: 'pointer', color: colors.primary.main, textDecoration: 'underline' }}
                                                onClick={() =>
                                                    navigate('/main/TaskMaster', {
                                                        state: { mode: 'edit', taskData: row },
                                                    })
                                                }
                                            >
                                                {row.TaskTitle || '—'}
                                            </Typography>

                                            {/* Assigned Info */}
                                            <Box sx={{ display: 'flex', gap: 2, mt: 0.4 }}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    To: {row.AssignedToName || '—'}
                                                </Typography>

                                                {row.AssignedByName && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        By: {row.AssignedByName}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Dates */}
                                            <Box sx={{ display: 'flex', flexDirection: "column", mt: 0.4 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Created: {row.CreatedAt}
                                                </Typography>

                                                <Typography variant="caption" color="text.secondary">
                                                    Due:{' '}
                                                    {row.DueDateTime
                                                        ? new Date(row.DueDateTime).toLocaleString('en-GB')
                                                        : '—'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* RIGHT SIDE – Priority + Status */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexDirection: "column",
                                            gap: 1,
                                            mt: 0.5
                                        }}
                                    >
                                        <Typography variant="body2">
                                            Priority:{" "}<span style={{ color, fontWeight: 600 }}>  {row.Priority || '—'}</span>
                                        </Typography>

                                        <Typography variant="body2">
                                            Status:{" "}
                                            <Chip
                                                label={row.Status || '—'}
                                                size="small"
                                                sx={{ fontWeight: 600, ...chipStyles }}
                                            />
                                        </Typography>

                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                    );
                })}

                {/* Pagination */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                        py: 2
                    }}
                >
                    <Button
                        variant="outlined"
                        disabled={paginationModel.page === 0}
                        onClick={() =>
                            setPaginationModel((p) => ({
                                ...p,
                                page: Math.max(0, p.page - 1)
                            }))
                        }
                    >
                        ← Previous
                    </Button>

                    <Typography variant="body2">
                        Page {paginationModel.page + 1} of{' '}
                        {Math.ceil(totalCount / paginationModel.pageSize) || 1}
                    </Typography>

                    <Button
                        variant="outlined"
                        disabled={
                            (paginationModel.page + 1) * paginationModel.pageSize >= totalCount
                        }
                        onClick={() =>
                            setPaginationModel((p) => ({
                                ...p,
                                page: p.page + 1
                            }))
                        }
                    >
                        Next →
                    </Button>
                </Box>
            </>
        );
    };

    return (
        <Box sx={{ pt: 1, pr: 1, ml: 2 }}>
            <Grid container alignItems="center" spacing={2}>
                <Grid item xs={4} sm={2}>
                    <Tooltip title="Add Task">
                        <IconButton
                            sx={{ color: 'orange' }}
                            onClick={() =>
                                navigate('/main/TaskMaster', {
                                    state: { mode: 'insert' },
                                })
                            }
                        >
                            <VscNewFile />
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid item xs={4} sm={8} sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom>
                        Task List
                    </Typography>
                </Grid>
                <Grid item xs={4} sm={2} sx={{ textAlign: 'right' }} />
            </Grid>

            <Paper sx={{ mt: 1, bgcolor: themeColors.neutral.surface, borderRadius: 2 }}>
                <Box sx={{ height: "81.5vh", maxWidth: '100%', overflow: 'auto' }}>
                    {/* {loading ? <CenterLoader /> :
                        rows?.length === 0 ? <NoDataComponent /> :
                            <DataGrid
                                // autoHeight
                                getRowHeight={() => 'auto'}
                                rows={rows}
                                columns={columns}
                                getRowId={(r) => r.TaskId}
                                checkboxSelection
                                // selectionModel={selectionModel}
                                rowSelectionModel={selectionModel}
                                onRowSelectionModelChange={(newSel) => {
                                    const ids = newSel.map(Number);
                                    setSelectionModel(ids);
                                    selectedIdsRef.current = ids; // snapshot
                                }}
                                disableRowSelectionOnClick

                                loading={loading}
                                pagination


                                rowCount={totalCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                                paginationMode="server"
                                pageSizeOptions={[50, 100]}
                                sx={{
                                    '& .MuiDataGrid-columnHeaders': { backgroundColor: themeColors.neutral.gridHeader, borderBottom: `1px solid ${themeColors.neutral.border}` },
                                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                    '& .MuiDataGrid-columnHeader': { backgroundColor: themeColors.neutral.gridHeader, color: themeColors.neutral.ink, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
                                    '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                                    '& .MuiDataGrid-scrollbarFiller': { backgroundColor: themeColors.neutral.gridHeader },
                                    '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface },
                                    '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.neutral.surfaceAlt },
                                    '& .MuiDataGrid-row:hover': { backgroundColor: themeColors.primary.tint },
                                    '& .MuiDataGrid-row.Mui-selected': { backgroundColor: `${themeColors.primary.selected} !important` },
                                    '& .MuiDataGrid-cell': {
                                        display: 'flex',
                                        alignItems: 'center'
                                    },
                                }}
                                slots={{
                                    noResultsOverlay: CustomNoDataOverlay
                                }}
                            />} */}
                    {loading ? (
                        <CenterLoader />
                    ) : rows?.length === 0 ? (
                        <NoDataComponent />
                    ) : isMobile ? (
                        <>
                            <Box sx={{ mt: 1, mb: 2, px: 1 }}>
                                <TextField fullWidth size="small" placeholder="Search Tasks..." value={mobileSearchQuery} onChange={(e) => setMobileSearchQuery(e.target.value)} variant="outlined"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            {role !== 'User' && selectionModel.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }} >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox checked={selectAll} onChange={toggleSelectAll} size="small" />
                                        <Typography variant="body2" sx={{ ml: 1 }}>
                                            {selectionModel.length} selected
                                        </Typography>
                                    </Box>
                                    <Tooltip title="Delete selected">
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setConfirmOpen(true);
                                            }} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}

                            <Box sx={{ width: '100%', overflowX: 'hidden', px: 1 }}>
                                {renderMobileCards()}
                            </Box>
                        </>
                    ) : (
                        <DataGrid
                            // autoHeight
                            getRowHeight={() => 'auto'}
                            rows={rows}
                            columns={columns}
                            getRowId={(r) => r.TaskId}
                            checkboxSelection
                            // selectionModel={selectionModel}
                            rowSelectionModel={selectionModel}
                            onRowSelectionModelChange={(newSel) => {
                                const ids = newSel.map(Number);
                                setSelectionModel(ids);
                                selectedIdsRef.current = ids; // snapshot
                            }}
                            disableRowSelectionOnClick

                            loading={loading}
                            pagination


                            rowCount={totalCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                            paginationMode="server"
                                pageSizeOptions={[50, 100]}
                            sx={{
                                '& .MuiDataGrid-columnHeaders': { backgroundColor: themeColors.neutral.gridHeader, borderBottom: `1px solid ${themeColors.neutral.border}` },
                                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                '& .MuiDataGrid-columnHeader': { backgroundColor: themeColors.neutral.gridHeader, color: themeColors.neutral.ink, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
                                '& .MuiDataGrid-filler': { backgroundColor: themeColors.neutral.surface },
                                '& .MuiDataGrid-scrollbarFiller': { backgroundColor: themeColors.neutral.gridHeader },
                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface },
                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.neutral.surfaceAlt },
                                '& .MuiDataGrid-row:hover': { backgroundColor: themeColors.primary.tint },
                                '& .MuiDataGrid-row.Mui-selected': { backgroundColor: `${themeColors.primary.selected} !important` },
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center'
                                },
                            }}
                            slots={{
                                noResultsOverlay: CustomNoDataOverlay
                            }}
                        />
                    )}

                </Box>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Dialog open={confirmOpen} onClose={() => { if (!deleting) setConfirmOpen(false); }} maxWidth="xs" fullWidth>
                {deleting
                    ? <LinearProgress color="error" sx={{ height: 4 }} />
                    : <Box sx={{ height: 4, background: gradients.danger }} />}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>
                        Confirm Deletion
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete{' '}
                        <Typography component="span" variant="body2" fontWeight={700} color="error.main">
                            {selectedIdsRef.current.length}
                        </Typography>
                        {' '}task(s)? This action cannot be undone.
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} variant="outlined" color="inherit" size="small" disabled={deleting}>Cancel</Button>
                    <Button color="error" variant="contained" size="small" disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                        onClick={handleDelete}>{deleting ? 'Deleting…' : 'Delete'}</Button>
                </Box>
            </Dialog>

        </Box>
    );
}
